/**
 * Deck theme tokens — every value is a CSS variable reference so per-advisor
 * branding (styles/applyBranding.ts) repaints the whole deck. Never put brand
 * hex literals in components; add a var to styles/globals.css instead.
 *
 * Naming note: keys keep the MOCKUP's role names (navy/teal/...) so the
 * ported components read like the prototype, but they map onto the brand
 * vars (teal → accent/amber, navy → dark/dusk, etc.).
 */
export const T = {
  navy: 'var(--color-dark)',
  navySoft: 'var(--color-dark-soft)',
  teal: 'var(--color-accent)',
  tealSoft: 'var(--color-accent-soft)',
  tealDeep: 'var(--color-accent-deep)',
  blue: 'var(--color-clay)',
  gray: 'var(--color-gray)',
  grayLine: 'var(--color-cream-soft)',
  bg: 'var(--color-cream)',
  card: 'var(--color-card)',
  ink: 'var(--color-ink)',
  paper: 'var(--color-paper)',
  growth: 'var(--color-growth)',
  growthSoft: 'var(--color-growth-soft)',
  chartPension: 'var(--chart-pension)',
  chartWages: 'var(--chart-wages)',
} as const;

export const FONT = 'var(--font-sans)';
export const SERIF = 'var(--font-display)';

/** Translucent mix of a themed color — replaces the mockup's rgba() literals. */
export const mix = (cssVar: string, pct: number) =>
  `color-mix(in srgb, ${cssVar} ${pct}%, transparent)`;

/**
 * Resolve themed colors to concrete values for SVG/recharts (which set
 * colors as presentation attributes where var() isn't reliable).
 * Read lazily at render so applyBranding overrides are picked up.
 */
export function resolveChartColors() {
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  return {
    navy: read('--color-dark', '#1F2B2F'),
    teal: read('--color-accent', '#C3861D'),
    blue: read('--color-clay', '#C0A378'),
    gray: read('--color-gray', '#7C7464'),
    grayLine: read('--color-cream-soft', '#DED2AB'),
    card: read('--color-card', '#FBF7EC'),
    growth: read('--color-growth', '#3E7C5A'),
    growthSoft: read('--color-growth-soft', '#CFE3D6'),
    pension: read('--chart-pension', '#9B8FC9'),
    wages: read('--chart-wages', '#E0A33B'),
  };
}

export type ChartColors = ReturnType<typeof resolveChartColors>;

/** Currency helpers shared by the deck (mirrors the mockup's fmt/fmtFull). */
export const fmtMoney = (n: number): string =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `$${(n / 1000).toFixed(0)}K`
      : `$${Math.round(n)}`;

export const fmtMoneyFull = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
